import { apiRequestExternal } from "./apiRequestExternal";
import { apiValidateArguments } from "./apiValidateArguments";
import { apiFilterNonNull } from "./apiFilterNonNull";
import { localLogger, logger } from "../util/logger";
import { getStoredPartnerCode } from "../util/partnerProgram";
import { getEffectiveUtmSource, getStoredUtmCampaign, getStoredUtmMedium } from "../util/utmSource";
import { getRedditClickIdFromBrowser } from "../lib/reddit/cookie";
import { getGoogleClickIdFromBrowser } from "../lib/google/cookie";
import {
  getGaClientIdFromBrowser,
  resolveGaClientIdForSignup,
} from "../lib/google/ga-cookie";
import { readStoredConsent } from "../lib/analytics";

function composerBrowserBaseUrl() {
  if (typeof window !== "undefined") {
    return "/api/composer";
  }
  return process.env.NEXT_PUBLIC_COMPOSER_URL;
}

// Start OAuth process for signing up/in with provider
// This is not absolute, but is called from <SignupModal/> => <AuthSection/> => <Auth/> => <AuthSocial/> => signInWithProvider from auth.js
export const startOAuthProcess = async (
  provider,
  params,
  afterAuthPath = "",
  adminToken = process.env.NEXT_PUBLIC_PROJECT_ID
) => {
  try {
    apiValidateArguments({ provider, adminToken });

    const validProviders = ["github", "google", "github.com", "google.com"];

    if (!validProviders.includes(provider)) {
      throw new Error(
        `Invalid provider—expecting one of: ${validProviders.join(", ")}`,
        provider
      );
    }

    // Capture the current location's pathname and search params (query string)
    const currentRoute =
      afterAuthPath || window.location.pathname + window.location.search;

    // URL-encode it to make it safe for use in the URL
    const encodedRoute = encodeURIComponent(currentRoute);

    // Base URL and provider string
    const baseUrl = `${composerBrowserBaseUrl()}/v1/public/global/get-auth/oauth`;
    const normalizedProvider = provider.replace(".com", "");
    const providerString = `provider_name=${normalizedProvider}.com`;

    // Merge page query + stored partner ref so Composer oauth cookies include `ref`
    const qp = new URLSearchParams();
    if (typeof params === "string" && params.length > 0) {
      const raw = params.startsWith("?") ? params.slice(1) : params;
      if (raw) {
        new URLSearchParams(raw).forEach((v, k) => {
          qp.set(k, v);
        });
      }
    }
    const storedRef = getStoredPartnerCode();
    if (storedRef) {
      qp.set("ref", storedRef);
    }
    // Carry Reddit click-ID (rdt_cid) into the OAuth callback so server-side
    // register_user() can persist it on the new user document, regardless
    // of which page the user initiated OAuth from.
    if (!qp.has("rdt_cid")) {
      const redditClickId = getRedditClickIdFromBrowser();
      if (redditClickId) {
        qp.set("rdt_cid", redditClickId);
      }
    }
    // Mirror the Reddit click-ID logic for Google's `gclid` so that GA4
    // Measurement Protocol events fired by Composer after OAuth signup
    // carry click-level attribution for Google Ads bidding.
    if (!qp.has("gclid")) {
      const googleClickId = getGoogleClickIdFromBrowser();
      if (googleClickId) {
        qp.set("gclid", googleClickId);
      }
    }
    // Carry the GA4 MP `client_id` (from the `_ga` cookie set by the GA4
    // JavaScript library) so server-side MP events fired by Composer use
    // the same client identifier the browser-side `gtag` calls use —
    // otherwise GA4 would treat web vs. server hits as different users.
    // Presence of `_ga` already implies `analytics_storage` was granted;
    // no cookie means the caller declined analytics and we omit the param.
    if (!qp.has("ga_client_id")) {
      const gaClientId = getGaClientIdFromBrowser();
      if (gaClientId) {
        qp.set("ga_client_id", gaClientId);
      }
    }
    if (!qp.has("utm_source")) {
      const utmSource = getEffectiveUtmSource();
      if (utmSource) {
        qp.set("utm_source", utmSource);
      }
    }
    if (!qp.has("utm_medium")) {
      const utmMedium = getStoredUtmMedium();
      if (utmMedium) {
        qp.set("utm_medium", utmMedium);
      }
    }
    if (!qp.has("utm_campaign")) {
      const utmCampaign = getStoredUtmCampaign();
      if (utmCampaign) {
        qp.set("utm_campaign", utmCampaign);
      }
    }
    // Send the current browser origin so Composer routes the provider callback back to
    // the same host (public vs. admin portal). Required because the same-origin
    // /api/composer proxy hides the browser Origin/Referer from Composer. Composer
    // validates this against a configured allowlist before using it in a redirect.
    const webOrigin =
      typeof window !== "undefined" ? window.location.origin : "";
    if (webOrigin) {
      qp.set("web_origin", webOrigin);
    }

    const extraQs = qp.toString();

    const url = `${baseUrl}?${providerString}&initiation_route=${encodedRoute}${
      extraQs ? `&${extraQs}` : ""
    }`;

    const response = await apiRequestExternal(
      url,
      "GET",
      null,
      { Authorization: adminToken },
      true,
      false
    );

    localLogger("response in startOAuthProcess function:", response);

    const oAuthUrl = response.data.authorization_url;
    try {
      // Keep a short-lived provider hint to recover callbacks that omit provider.
      sessionStorage.setItem("oauth_provider_hint", normalizedProvider);
    } catch {
      // Ignore storage errors and continue redirect.
    }

    window.location.href = oAuthUrl;
  } catch (err) {
    console.error("Error initiating OAuth:", err);
    throw err;
  }
};

// Submit email validation token
export const submitEmailToken = async (
  email,
  emailToken,
  adminToken = process.env.NEXT_PUBLIC_PROJECT_ID
) => {
  try {
    // Validate args
    apiValidateArguments({ email, emailToken, adminToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/global/get-auth/validate_account_validation_token`,
      "POST",
      { email: email, token: emailToken },
      { Authorization: adminToken },
      false,
      false
    );

    localLogger("Response in submitEmailToken function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in submitEmailToken function:", err);
    throw err;
  }
};

// Resend email validation token email
export const resendEmailToken = async (
  email,
  adminToken = process.env.NEXT_PUBLIC_PROJECT_ID
) => {
  try {
    // Validate args
    apiValidateArguments({ email, adminToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/global/get-auth/email_account_validation_token`,
      "POST",
      { email },
      { Authorization: adminToken },
      false,
      false,
      2
    );

    localLogger("Response in resendEmailToken function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in resendEmailToken function:", err);
    throw err;
  }
};

// get regions
export async function getRegions(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/supported_regions`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    logger("Response in getRegions function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getRegions function:", error);
    throw error;
  }
}

// create account / register
export async function createUser(
  email,
  password,
  params,
  adminToken = process.env.NEXT_PUBLIC_PROJECT_ID
) {
  try {
    apiValidateArguments({ email, password, adminToken });

    const ref = getStoredPartnerCode();
    const body = { email, password };
    if (ref) {
      body.ref = ref;
    }
    // Forward the two independent Consent Mode v2 channels (ad_storage
    // and analytics_storage) captured by the banner so Composer can
    // persist them on the new user doc and use them to gate server-side
    // events at conversion time:
    //   - ad_storage   -> Reddit CAPI + Google Ads conversion events
    //   - analytics_storage -> GA4 Measurement Protocol events
    // An explicit undefined is fine — Composer treats missing values as
    // "not yet decided" and withholds marketing/analytics events.
    const storedConsent = readStoredConsent();
    if (storedConsent?.ad_storage === "granted") {
      body.ad_consent_granted = true;
    } else if (storedConsent?.ad_storage === "denied") {
      body.ad_consent_granted = false;
    }
    if (storedConsent?.analytics_storage === "granted") {
      body.analytics_consent_granted = true;
    } else if (storedConsent?.analytics_storage === "denied") {
      body.analytics_consent_granted = false;
    }

    // Forward the Reddit click-ID (rdt_cid) captured by our middleware into
    // a first-party cookie, so web-only signups record the same Reddit ad
    // attribution that the desktop-installer flow already records. We pass
    // it as a URL query param alongside the existing UTM params so
    // Composer's register_user() picks it up through its url_params path.
    // Same treatment for Google's `gclid` so GA4 Measurement Protocol
    // events carry Google Ads click-level attribution for bidding.
    const redditClickId = getRedditClickIdFromBrowser();
    const googleClickId = getGoogleClickIdFromBrowser();
    // Web-originated MP client_id: parsed out of the `_ga` cookie the
    // GA4 JS library wrote once the user granted `analytics_storage`.
    // Forwarded so Composer can populate `users.ga_client_id` and
    // server-side MP events (sign_up / purchase) share the same client
    // identifier as browser-side `gtag` hits.
    const gaClientId =
      storedConsent?.analytics_storage === "granted"
        ? await resolveGaClientIdForSignup({
            measurementId: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,
          })
        : getGaClientIdFromBrowser();
    const utmSource = getEffectiveUtmSource();
    const utmMedium = getStoredUtmMedium();
    const utmCampaign = getStoredUtmCampaign();
    let effectiveParams = params || "";
    if (redditClickId || googleClickId || gaClientId || utmSource || utmMedium || utmCampaign) {
      const hasRaw = typeof effectiveParams === "string" && effectiveParams.length > 0;
      const raw = hasRaw
        ? (effectiveParams.startsWith("?")
            ? effectiveParams.slice(1)
            : effectiveParams)
        : "";
      const qp = new URLSearchParams(raw);
      if (redditClickId && !qp.has("rdt_cid")) {
        qp.set("rdt_cid", redditClickId);
      }
      if (googleClickId && !qp.has("gclid")) {
        qp.set("gclid", googleClickId);
      }
      if (gaClientId && !qp.has("ga_client_id")) {
        qp.set("ga_client_id", gaClientId);
      }
      if (utmSource && !qp.has("utm_source")) {
        qp.set("utm_source", utmSource);
      }
      if (utmMedium && !qp.has("utm_medium")) {
        qp.set("utm_medium", utmMedium);
      }
      if (utmCampaign && !qp.has("utm_campaign")) {
        qp.set("utm_campaign", utmCampaign);
      }
      effectiveParams = `?${qp.toString()}`;
    }

    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/global/get-auth/register${effectiveParams}`,
      "POST",
      body,
      { Authorization: adminToken },
      false,
      false
    );

    localLogger("Response in createUser function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in createUser function:", error);
    throw error;
  }
}

// check access key
export async function checkAccessKey(
  accessKey,
  adminToken = process.env.NEXT_PUBLIC_PROJECT_ID
) {
  try {
    // Validate args
    apiValidateArguments({ accessKey, adminToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/early_access_check`,
      "POST",
      { early_access_key: accessKey },
      { Authorization: adminToken },
      false,
      false
    );

    localLogger("Response in checkAccessKey function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in checkAccessKey function:", error);
    throw error;
  }
}

// get public user profile info
export async function getUserProfile(
  token = process.env.NEXT_PUBLIC_PROJECT_ID,
  displayName
) {
  try {
    // Validate args
    if (!token) throw new Error("Missing access token.");

    const encodedDisplayName = encodeURIComponent(displayName);
    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/profile/public?display_name=${encodedDisplayName}`,
      "GET",
      null,
      { Authorization: token },
      false,
      false
    );

    localLogger("Response in getUserProfile function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getUserProfile function:", error);
    throw error;
  }
}

// Get all avatar image urls for user profiles
export async function getAvatarLinks(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/profile/avatar_options`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );

    localLogger("Response in getAvatarLinks function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getAvatarLinks function:", error);
    throw error;
  }
}

// get user by Firebase Auth uid (via token)
export async function getUserByToken(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/profile/by_token`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );

    localLogger("Response in getUserByToken function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getUserByToken function:", error);
    throw error;
  }
}

/**
 * Fetch total user-owned GCS usage and the effective subscription quota.
 */
export async function getUserStorageUsage(accessToken) {
  if (!accessToken) {
    throw new Error("Missing access token.");
  }

  return apiRequestExternal(
    `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/data_size`,
    "GET",
    null,
    { Authorization: accessToken },
    false,
    false
  );
}

// update user
export async function updateUser(
  accessToken,
  email,
  username,
  description,
  avatar,
  region
) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Filter to only pass provided args in request body
    const body = apiFilterNonNull({
      email,
      display_name: username,
      description,
      avatar,
      region,
    });

    if (Object.keys(body).length === 0) {
      throw new Error("No new data provided to update user.");
    }

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/profile`,
      "PUT",
      body,
      { Authorization: accessToken },
      false,
      false
    );

    localLogger("Response in updateUser function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in updateUser function:", error);
    throw error;
  }
}

// get all of a user's experiments
export async function getUserExperiments(accessToken, skip = 0, limit = 10) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/experiments?skip=${skip}&limit=${limit}`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    logger("Response in getUserExperiments function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getUserExperiments function:", error);
    throw error;
  }
}

// tell backend that user is active
export async function updateUserHeartbeat(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/user_session/heartbeat`,
      "PUT",
      null,
      { Authorization: accessToken }
    );

    logger("Response in updateUserHeartbeat function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in updateUserHeartbeat function:", error);
    throw error;
  }
}

// fetch latest user roles state
export async function getUserRoles(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    const composerBase = composerBrowserBaseUrl()?.replace(/\/$/, "");
    if (!composerBase) {
      throw new Error("Composer base URL is not configured.");
    }

    // Return custom response data
    const response = await apiRequestExternal(
      `${composerBase}/v1/public/regional/auth/user/users/roles`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );

    localLogger("Response in getUserRoles function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getUserRoles function:", error);
    throw error;
  }
}

// fetch effective subscription tier (includes team seat unlock, which is never stored in roles)
export async function getUserSubscriptionTier(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    const composerBase = composerBrowserBaseUrl()?.replace(/\/$/, "");
    if (!composerBase) {
      throw new Error("Composer base URL is not configured.");
    }

    // Return custom response data
    const response = await apiRequestExternal(
      `${composerBase}/v1/public/regional/auth/user/users/subscription_tier`,
      "GET",
      null,
      { Authorization: accessToken },
      false,
      false
    );

    localLogger("Response in getUserSubscriptionTier function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in getUserSubscriptionTier function:", error);
    throw error;
  }
}

// ask backend if user is active via other tabs/devices
export async function checkUserHeartbeat(accessToken) {
  try {
    // Validate args
    if (!accessToken) throw new Error("Missing access token.");

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/user_session/last_active`,
      "POST",
      null,
      { Authorization: accessToken }
    );

    logger("Response in checkUserHeartbeat function:", response);
    return response;

    // Handle errors
  } catch (error) {
    console.error("Error in checkUserHeartbeat function:", error);
    throw error;
  }
}

// Send validation token for new email of existing user
export const sendUserEmailToken = async (accessToken, email) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken, email });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/email_account_validation_token`,
      "POST",
      { email },
      { Authorization: accessToken }
    );

    localLogger("Response in sendUserEmailToken function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in sendUserEmailToken function:", err);
    throw err;
  }
};

// Change email
export const changeUserEmail = async (accessToken, email, emailToken) => {
  try {
    // Validate args
    apiValidateArguments({ email, emailToken, accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/update_user_email`,
      "POST",
      { email, token: emailToken },
      { Authorization: accessToken }
    );

    localLogger("Response in changeUserEmail function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in changeUserEmail function:", err);
    throw err;
  }
};

// Update tutorial progress
export const updateTutorialProgress = async (
  accessToken,
  completedChapterId
) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken, completedChapterId });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/update_tutorial_progress`,
      "PUT",
      { completed_chapter_id: completedChapterId },
      { Authorization: accessToken }
    );

    localLogger("Response in updateTutorialProgress function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in updateTutorialProgress function:", err);
    throw err;
  }
};

export const unlockAdvanced = async (accessToken) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/show_advanced_tutorials`,
      "POST",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in unlockAdvanced function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in unlockAdvanced function:", err);
    throw err;
  }
};

// Search for a user
export const getUser = async (accessToken, displayName) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken, displayName });

    const encodedDisplayName = encodeURIComponent(displayName);
    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/query?display_name=${encodedDisplayName}`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in getUser function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in getUser function:", err);
    throw err;
  }
};

// Stop all user experiments/plays
export const shutEmDown = async (accessToken) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/stop_running_sessions`,
      "POST",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in shutEmDown function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in shutEmDown function:", err);
    throw err;
  }
};

// Confirm agreement to privacy/legal checkboxes
export const givePrivacyConsent = async (accessToken) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/consent_to_latest_terms_and_policy`,
      "POST",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in givePrivacyConsent function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in givePrivacyConsent function:", err);
    throw err;
  }
};

export const getAPIKey = async (accessToken) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/api_key`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in getAPIKey function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in getAPIKey function:", err);
    throw err;
  }
};

// Get all user purchases
export const getUserPurchases = async (accessToken) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/purchases`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in getUserPurchases function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in getUserPurchases function:", err);
    throw err;
  }
};

// Get trainer upload url(s)
// example filesArray:
// [
//   {
//     "file_type": "string", // "csv", "image", "video"
//     "filename": "string",
//     "folder": "string",
//     "content_type": "string"
//   }
// ]
export const generateTrainerUploadURLs = async (accessToken, filesArray) => {
  try {
    // Validate args
    if (!accessToken || !Array.isArray(filesArray) || !filesArray.length) {
      throw new Error("Missing required arguments.");
    }

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/generate_upload_url`,
      "POST",
      filesArray,
      {
        Authorization: accessToken,
      }
    );

    localLogger("Response in generateTrainerUploadURLs function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in generateTrainerUploadURLs function:", err);
    throw err;
  }
};

/**
 * Finalize a completed direct trainer upload against its quota reservation.
 */
export const finalizeTrainerUpload = async (accessToken, reservationId) => {
  apiValidateArguments({ accessToken, reservationId });
  return apiRequestExternal(
    `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/finalize_upload`,
    "POST",
    { reservation_id: reservationId },
    { Authorization: accessToken }
  );
};

// Upload trainer data
export const uploadTrainerData = async (
  fileType, // "csv", "image", "video"
  folder,
  file,
  accessToken
) => {
  try {
    // Validate args
    apiValidateArguments({ fileType, file, folder, accessToken });

    const url = `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/upload_trainer_data?file_type=${fileType}&folder=${folder}`;

    // Return custom response data
    const response = await apiRequestExternal(
      url,
      "POST",
      file,
      {
        Authorization: accessToken,
      },
      false,
      false,
      false
    );

    localLogger("Response in uploadTrainerData function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in uploadTrainerData function:", err);
    throw err;
  }
};

// Get all user trainer directories
export const getUserTrainerFilePaths = async (accessToken, path) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken });

    const url = `${
      process.env.NEXT_PUBLIC_COMPOSER_URL
    }/v1/public/regional/auth/user/users/trainer_listing${
      path && "?path=" + path
    }`;

    // Return custom response data
    const response = await apiRequestExternal(url, "GET", null, {
      Authorization: accessToken,
    });

    localLogger("Response in getUserTrainerFilePaths function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in getUserTrainerFilePaths function:", err);
    throw err;
  }
};

// Get a user's uploaded trainer file itself
export const getUserTrainerFile = async (accessToken, path) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken, path });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/trainer_data?file_path=${path}`,
      "GET",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in getUserTrainerFile function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in getUserTrainerFile function:", err);
    throw err;
  }
};

// Delete trainer file
export const deleteUserTrainerFile = async (accessToken, path) => {
  try {
    // Validate args
    apiValidateArguments({ accessToken, path });

    // Return custom response data
    const response = await apiRequestExternal(
      `${process.env.NEXT_PUBLIC_COMPOSER_URL}/v1/public/regional/auth/user/users/file?file_full_path=${path}`,
      "DELETE",
      null,
      { Authorization: accessToken }
    );

    localLogger("Response in deleteUserTrainerFile function:", response);
    return response;

    // Handle errors
  } catch (err) {
    console.error("Error in deleteUserTrainerFile function:", err);
    throw err;
  }
};
